"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import { usePathname } from "@/hooks/use-pathname";
import { motion, AnimatePresence, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { Menu, X } from "lucide-react";
import ThemeSwitcher from "./settings/theme-switcher";
import { useLanguage } from "@/context/language-context";
import { useLenis } from "@/components/smooth-scroll";
import { useActiveSection, type SectionId } from "@/hooks/use-active-section";
import { useDocumentReady } from "@/hooks/use-document-ready";
import { NavUnderline } from "@/components/nav-underline";

export default function Navbar() {
  const { content } = useLanguage();
  const pathname = usePathname();
  const lenis = useLenis();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [screenWidth, setScreenWidth] = useState(1920);
  const [containerWidth, setContainerWidth] = useState(1280);
  const [scrollHeight, setScrollHeight] = useState(800);
  const dummyRef = useRef<HTMLDivElement>(null);

  const { scrollY } = useScroll();

  const bgOpacity = useTransform(scrollY, (val) => Math.min(val / scrollHeight, 1));
  const backdropBlur = useTransform(scrollY, (val) => Math.min(val / scrollHeight, 1) * 16);
  const backdropFilter = useMotionTemplate`blur(${backdropBlur}px)`;

  const py = useTransform(scrollY, (val) => {
    const ratio = Math.min(val / scrollHeight, 1);
    return 24 - ratio * 12;
  });

  const startWidth = Math.max(screenWidth, containerWidth);
  const navMaxWidth = useTransform(scrollY, (val) => {
    const ratio = Math.min(val / scrollHeight, 1);
    return startWidth - ratio * (startWidth - containerWidth);
  });

  const sectionIds = useMemo<SectionId[]>(
    () => ["home", "about", "stack", "roadmap", "projects", "contact"],
    [],
  );

  const navLinks = [
    { name: content.nav.home, href: "/#home", sectionId: "home" as SectionId },
    { name: content.nav.about, href: "/#about", sectionId: "about" as SectionId },
    { name: content.nav.stack, href: "/#stack", sectionId: "stack" as SectionId },
    { name: content.nav.roadmap, href: "/#roadmap", sectionId: "roadmap" as SectionId },
    { name: content.nav.projects, href: "/#projects", sectionId: "projects" as SectionId },
    { name: content.nav.contact, href: "/#contact", sectionId: "contact" as SectionId },
  ];

  const documentReady = useDocumentReady();
  const { activeId, progress } = useActiveSection(sectionIds, lenis);
  const [hoveredSection, setHoveredSection] = useState<SectionId | null>(null);

  useEffect(() => {
    if (!documentReady) return;

    const updateDimensions = () => {
      setScreenWidth(window.innerWidth);
      setScrollHeight(window.innerHeight);
      if (dummyRef.current) {
        setContainerWidth(dummyRef.current.getBoundingClientRect().width);
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [documentReady]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      lenis?.stop();
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      lenis?.start();
    }

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      lenis?.start();
    };
  }, [isMobileMenuOpen, lenis]);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.replace(/^\/?#/, "");
    const onHomePage = pathname === "/";

    if (!onHomePage) {
      setIsMobileMenuOpen(false);
      window.location.assign(href);
      return;
    }

    const elem = targetId === "home" ? null : document.getElementById(targetId);

    if (elem || targetId === "home") {
      setIsMobileMenuOpen(false);

      setTimeout(() => {
        if (lenis) {
          lenis.scrollTo(targetId === "home" ? 0 : elem!, {
            offset: targetId === "home" ? 0 : -80,
            duration: 1.5,
          });
        } else if (targetId === "home") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          elem?.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  return (
    <motion.header
      style={{
        paddingTop: py,
        paddingBottom: py,
      }}
      className="fixed top-0 right-0 left-0 z-sticky transition-colors duration-300"
    >
      <div ref={dummyRef} className="pointer-events-none invisible absolute -z-50 container" />

      <motion.div
        style={{
          opacity: bgOpacity,
          backdropFilter,
          WebkitBackdropFilter: backdropFilter,
        }}
        className="pointer-events-none absolute inset-0 -z-10 border-b border-border/40 bg-background/75"
      />

      <motion.nav
        style={{
          maxWidth: navMaxWidth,
        }}
        className="mx-auto flex w-full items-center justify-between px-container"
      >
        <a
          href="/#home"
          onClick={(e) => scrollToSection(e, "/#home")}
          className="focus-ring group relative z-sticky flex items-center gap-2 rounded-sm"
        >
          <span className="text-xl font-black tracking-tighter text-foreground uppercase transition-all duration-300 group-hover:opacity-70 sm:text-2xl">
            {`${content.hero.name} ${content.hero.subtitle}`.toLowerCase()}
          </span>
        </a>

        <div className="hidden items-center gap-8 xl:flex">
          <ul className="flex items-center gap-6">
            {navLinks.map((link) => {
              const isActive = activeId === link.sectionId;
              const isHovered = hoveredSection === link.sectionId;
              return (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    onMouseEnter={() => setHoveredSection(link.sectionId)}
                    onMouseLeave={() => setHoveredSection(null)}
                    className={`focus-ring relative rounded-sm py-2 text-xs font-medium tracking-[0.2em] uppercase transition-colors ${
                      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {link.name}
                    <NavUnderline
                      isActive={isActive}
                      isHovered={isHovered}
                      progress={progress}
                      activeId={activeId}
                      className="bottom-0"
                    />
                  </a>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center gap-3">
            <ThemeSwitcher />
          </div>
        </div>

        <div className="flex items-center gap-4 xl:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="focus-ring relative z-sticky flex h-11 w-11 items-center justify-center text-foreground"
            aria-label="Toggle Menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-modal flex h-[100dvh] w-screen flex-col bg-background xl:hidden"
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(var(--primary-rgb),0.05),transparent)]" />
            <div className="bg-grid-white/[0.02] pointer-events-none absolute inset-0" />

            <div className="relative z-10 flex flex-1 flex-col overflow-y-auto px-container pt-24 pb-24 sm:pt-32 sm:pb-12">
              <ul className="flex flex-col gap-6 sm:gap-8">
                {navLinks.map((link, i) => {
                  const isActive = activeId === link.sectionId;
                  const isHovered = hoveredSection === link.sectionId;
                  return (
                    <motion.li
                      key={link.name}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.1 + i * 0.05,
                        duration: 0.5,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    >
                      <a
                        href={link.href}
                        onClick={(e) => scrollToSection(e, link.href)}
                        onMouseEnter={() => setHoveredSection(link.sectionId)}
                        onMouseLeave={() => setHoveredSection(null)}
                        className="focus-ring group relative flex flex-col items-start rounded-sm"
                      >
                        <span
                          className={`text-4xl font-black tracking-tighter uppercase transition-all duration-300 group-hover:pl-4 group-hover:text-primary ${
                            isActive ? "text-primary" : "text-foreground"
                          }`}
                        >
                          {link.name}
                        </span>
                        <NavUnderline
                          isActive={isActive}
                          isHovered={isHovered}
                          progress={progress}
                          activeId={activeId}
                          className="top-full mt-2"
                        />
                      </a>
                    </motion.li>
                  );
                })}
              </ul>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <ThemeSwitcher />
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
